import {
  SaasAdminSetPasswordDto,
  UserChangePasswordDto,
  UserLoginDto,
  UserRefreshTokenDto,
} from '@docbay/schemas';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { UsersActions } from 'application/constants/actions';
import {
  setUsersAuthSessionStorage,
  setUsersRefreshTokenSessionStorage,
} from 'application/sessionStorage/auth';
import { AxiosError } from 'axios';
import { authService } from 'common';
import { UsersAPI } from 'integration/api/users';

/**
 * Login a user and store their authentication information in session storage.
 *
 * @param loginData - The user's login data, including their username and password.
 * @returns A promise that resolves with the user's authentication information on success, or rejects with an error message on failure.
 */
export const usersLogin = createAsyncThunk(
  UsersActions.usersLogin,
  async (loginData: UserLoginDto, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.usersLogin(loginData);
      setUsersAuthSessionStorage(data);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Refresh a user's authentication token and store it in session storage.
 *
 * @param refreshTokenData - The user's refresh token data, including their refresh token.
 * @returns A promise that resolves with the refreshed authentication information on success, or rejects with an error message on failure.
 */
export const usersRefreshToken = createAsyncThunk(
  UsersActions.usersRefreshToken,
  async (refreshTokenData: UserRefreshTokenDto, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.usersRefreshToken(refreshTokenData);
      setUsersRefreshTokenSessionStorage(data);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Refresh a user's expired authentication token.
 *
 * @param refreshTokenData - The user's refresh token data, including their refresh token.
 * @returns A promise that resolves with the refreshed authentication information on success, or rejects with an error message on failure.
 */
export const usersRefreshExpiredToken = async (
  refreshTokenData: UserRefreshTokenDto,
) => {
  try {
    const data = await UsersAPI.usersRefreshToken(refreshTokenData);
    setUsersRefreshTokenSessionStorage(data.data);
    return data.data;
  } catch (e) {
    console.error(e);
    authService.cleanStorage();
  }
};

/**
 * Sign out a user and clean their authentication information from session storage.
 *
 * @returns A promise that resolves with the sign-out response data on success, or rejects with an error message on failure.
 */
export const signOut = createAsyncThunk(
  UsersActions.signOut,
  async (_, { rejectWithValue }) => {
    try {
      const { data: responseData } = await UsersAPI.signOut();
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Change a user's password in the SaaS environment.
 *
 * @param data - The user's password change data, including their current and new passwords.
 * @returns A promise that resolves with the password change response data on success, or rejects with an error message on failure.
 */
export const changePasswordSaas = createAsyncThunk(
  UsersActions.changePasswordSaas,
  async (data: UserChangePasswordDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await UsersAPI.changePasswordSaas(data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Create a user's password in the SaaS environment.
 *
 * @param data - The user's password creation data, including their new password.
 * @returns A promise that resolves with the password creation response data on success, or rejects with an error message on failure.
 */
export const createPassword = createAsyncThunk(
  UsersActions.createPassword,
  async (data: SaasAdminSetPasswordDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await UsersAPI.createPassword(data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
